import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { SelectBox } from "devextreme-react/select-box";
import moment from "moment";
import { MasterPage } from "../../Shared/master";
import ResponsiveRecords from "../../Shared/ResponsiveRecords";
import { DataGridSkeletonPageSimpleWithTitle } from "../../skeleton/GridSkeleton";
import { axiosGet, axiosPost } from "../../../apis/useAxios";
import { showErrorToast } from "../../../helpers/message";
import { stashGridState, popGridState } from "../../../helpers/gridStateCache";
import { DEFAULT_TILE, TIME_SLOTS, activitySearchText, byPickupTime, countActivities, filterActivities, isHotelDropOff } from "./crewActivityRules";
import CrewActivityFilterBar from "./CrewActivityFilterBar";
import CrewActivitySummary from "./CrewActivitySummary";
import CrewActivityGrid from "./CrewActivityGrid";
import CrewActivityCard from "./CrewActivityCard";
import ChangeHotelModal from "./ChangeHotelModal";
import ChangeStayLocationAddress from "./ChangeStayLocationAddress";
import "../../reservations/dispatch/dispatch.css";
import "./crewActivity.css";
import { usePermission } from "../../../helpers/permissions";

/** The change stay location action stays switched off (as before); its popup is kept in ChangeStayLocationAddress.jsx. */
export const ENABLE_CHANGE_STAY_LOCATION = false;

const airline = () => sessionStorage.getItem("session_airlineCode");
const today = () => moment().startOf("day").toDate();
const START = { employeeNumber: "", airportCode: "", hotelId: 0, from: today(), to: today() };

/**
 * Crew Activity: the crew's activities with their transport and hotel for a check-in date range (CrewActivity/schedule-list),
 * with tiles (Fly / Non fly / Change hotel), a time slot filter, a grid on desktop and cards on phones. Change hotel opens
 * its popup; the crew's mobile view opens /crew-activity-mobile and Back restores this page.
 */
export default function CrewActivityComponent() {
  const history = useHistory();
  const canEdit = usePermission("crew-activity").edit;
  const [filters, setFilters] = useState(START);
  const [applied, setApplied] = useState(START);
  const [airports, setAirports] = useState([]);
  const [hotels, setHotels] = useState({ list: [], loading: true });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [view, setView] = useState({ tile: DEFAULT_TILE, timeSlotId: 0 });
  const [popup, setPopup] = useState(null); // { kind: "hotel" | "stay", row }
  const gridRef = useRef(null);

  const loadHotels = useCallback(async (airportCode) => {
    setHotels((h) => ({ ...h, loading: true }));
    try {
      const r = await axiosGet("/Hotel/get-hotel-dropdown", { AirlineCode: airline(), AirportCode: airportCode });
      setHotels({ list: r?.payload ?? [], loading: false });
    } catch {
      setHotels({ list: [], loading: false });
    }
  }, []);

  const loadRows = useCallback(async (f) => {
    const from = moment(f.from ?? new Date()).startOf("day");
    const to = moment(f.to ?? f.from ?? new Date()).endOf("day"); // end stays empty until the second click
    const r = await axiosPost("CrewActivity/schedule-list", {
      AirlineCode: airline(),
      EmployeeNumber: f.employeeNumber,
      AirportCode: f.airportCode,
      HotelId: f.hotelId,
      ActivateStartDate: from.format("YYYY-MM-DD HH:mm:ss"),
      ActivateEndDate: to.format("YYYY-MM-DD HH:mm:ss"),
    });
    setRows(byPickupTime(r?.payload ?? []));
    setApplied({ ...f, from: from.toDate(), to: to.toDate() });
  }, []);

  useEffect(() => {
    // Back from crew-activity-mobile restores the page as it was instead of loading again.
    const cached = popGridState();
    if (cached && cached.page === "crew-activity") {
      setFilters(cached.filters);
      setApplied(cached.applied);
      setAirports(cached.airports);
      setHotels({ list: cached.hotels, loading: false });
      setRows(cached.rows);
      setView(cached.view);
      setLoading(false);
      return;
    }
    Promise.all([axiosGet(`Airport/get-airport-dropdown/${airline()}`).then((r) => setAirports(r?.payload ?? [])), loadHotels(""), loadRows(START)])
      .catch((err) => showErrorToast(err?.message || "Unable to load the crew activities."))
      .finally(() => setLoading(false));
  }, [loadHotels, loadRows]);

  const run = async (f) => {
    setSearching(true);
    try {
      await loadRows(f);
    } catch (err) {
      showErrorToast(err?.message || "Unable to load the crew activities.");
    } finally {
      setSearching(false);
    }
  };

  const shown = useMemo(() => filterActivities(rows, view), [rows, view]);
  const count = useMemo(() => countActivities(filterActivities(rows, { ...view, tile: "" })), [rows, view]);
  // Change hotel / stay need the Edit right on Crew Activity; without it only the view actions show.
  const actions = {
    stayEnabled: ENABLE_CHANGE_STAY_LOCATION,
    onChangeHotel: canEdit ? (row) => setPopup({ kind: "hotel", row }) : undefined,
    onChangeStay: canEdit ? (row) => setPopup({ kind: "stay", row }) : undefined,
    onMobile: (row) => {
      stashGridState({ page: "crew-activity", filters, applied, airports, hotels: hotels.list, rows, view });
      history.push("/crew-activity-mobile", { employeeNumber: row.employeeNumber, sourceUrl: "/crew-activity" });
    },
  };
  const range = `${moment(applied.from).format("DD/MM/YYYY")} – ${moment(applied.to).format("DD/MM/YYYY")}`;
  const reload = () => run(applied);

  return (
    <MasterPage title="Crew Activity">
      <div className="dsp-page cra-page">
        <CrewActivityFilterBar
          filters={filters}
          airports={airports}
          hotels={hotels.list}
          hotelsLoading={hotels.loading}
          searching={searching}
          onChange={(c) => setFilters((p) => ({ ...p, ...c }))}
          onAirport={(airportCode) => {
            setFilters((p) => ({ ...p, airportCode, hotelId: 0 }));
            loadHotels(airportCode);
          }}
          onSearch={() => run(filters)}
          onClear={() => {
            setFilters(START);
            loadHotels("");
          }}
        />

        {loading ? (
          <div className="dsp-panel">
            <DataGridSkeletonPageSimpleWithTitle />
          </div>
        ) : (
          <>
            <CrewActivitySummary
              count={count}
              tile={view.tile}
              onTile={(tile) => setView((v) => ({ ...v, tile }))}
              total={rows.length}
              allShown={!view.tile && !view.timeSlotId}
              onAll={() => setView({ tile: "", timeSlotId: 0 })}
            />
            <section className="dsp-panel" aria-label="Crew activities">
              <header className="dsp-panel__head">
                <h2 className="dsp-panel__title">
                  <i className="fas fa-clock" aria-hidden="true" /> Crew activity <small>{range}</small>
                </h2>
                <div className="dsp-panel__filters">
                  <SelectBox
                    className="dsp-mini-select"
                    dataSource={TIME_SLOTS}
                    displayExpr="name"
                    valueExpr="id"
                    value={view.timeSlotId}
                    inputAttr={{ "aria-label": "Time slot" }}
                    onValueChanged={(e) => setView((v) => ({ ...v, timeSlotId: e.value ?? 0 }))}
                  />
                </div>
              </header>
              <ResponsiveRecords
                records={shown}
                keyOf={(r, i) => `${r.transactionId}|${r.employeeNumber}|${r.id}|${i}`}
                searchText={activitySearchText}
                searchPlaceholder="Search activities"
                emptyText="No activity matches these filters."
                renderCard={(r) => <CrewActivityCard row={r} actions={actions} />}
                renderGrid={() => <CrewActivityGrid rows={shown} gridRef={gridRef} actions={actions} />}
              />
            </section>
          </>
        )}
      </div>

      {popup?.kind === "hotel" && <ChangeHotelModal row={popup.row} onChanged={reload} onClose={() => setPopup(null)} />}
      {popup?.kind === "stay" && (
        <ChangeStayLocationAddress
          isHotelDropoff={isHotelDropOff(popup.row)}
          airlineCode={popup.row.airlineCode}
          employeeNumber={popup.row.employeeNumber}
          airportCode={popup.row.airportCode}
          id={popup.row.id}
          hotelName={(isHotelDropOff(popup.row) ? popup.row.dropOffHotelName : popup.row.hotelName) || ""}
          isHotelCancel
          isTransporterRequired
          onRefresh={reload}
          onClose={() => setPopup(null)}
        />
      )}
    </MasterPage>
  );
}
